import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import {
  useCreateSalesOrderMutation,
  useGetSalesOrderQuery,
  useGetCustomersQuery,
  useRecordSalesPaymentMutation,
} from '../../../store/api/salesApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetLocationsQuery } from '../../../store/api/inventoryApi';
import { useGetUsersQuery } from '../../../store/api/userApi';
import { CustomerFormContent } from './CustomerForm';
import { selectUser } from '../../../store/slices/authSlice';
import { useGetMyActiveTripQuery } from '../../../store/api/vanSalesApi';
import Chip from '@mui/material/Chip';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

const DEFAULT_VAT_PERCENT = 5;

function SalesOrderForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isView = !!id && id !== 'new';

  const currentUser = useSelector(selectUser);

  const [createSalesOrder] = useCreateSalesOrderMutation();
  const { data: customers } = useGetCustomersQuery();
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] =
    useLazyGetProductsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: locations } = useGetLocationsQuery();
  const { data: usersData } = useGetUsersQuery();
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const userList = Array.isArray(usersData) ? usersData : usersData?.users ?? [];
  const { data: existingOrder, isLoading: loadingOrder } = useGetSalesOrderQuery(id, {
    skip: !isView,
  });
  const { data: activeVanTrip } = useGetMyActiveTripQuery(undefined, { skip: isView });

  const vanMode = !isView && activeVanTrip && activeVanTrip.id;
  const vanLocationId = activeVanTrip?.van?.locationId || activeVanTrip?.van?.location?.id;
  const vanName = activeVanTrip?.van?.name;
  const vanPlate = activeVanTrip?.van?.plateNumber;
  const vanTripNumber = activeVanTrip?.tripNumber;

  const currency = company?.currency || 'USD';
  const vatIncluded = company?.salesVatIncluded !== false;
  const defaultVat = Number(company?.vatRate) || DEFAULT_VAT_PERCENT;

  const [formData, setFormData] = useState(() => ({
    customerId: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    orderDate: new Date().toISOString().split('T')[0],
    salesType: 'credit',
    creditDays: '',
    salesPersonId: currentUser?.id || '',
    locationId: '',
    notes: '',
  }));

  const creditDaysOptions = (company?.creditDaysOptions || '7,14,30')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0);
  const defaultCreditDays = creditDaysOptions.length > 0 ? creditDaysOptions[0] : null;

  const [items, setItems] = useState([]);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [payments, setPayments] = useState(
    !!id && id !== 'new'
      ? []
      : [
          {
            method: 'cash',
            paymentAccountId: '',
            referenceNo: '',
            amount: '',
          },
        ]
  );
  const submitModeRef = useRef('save'); // 'save' | 'save-print' | 'quotation' - read in handleSubmit
  const [receivePaymentOpen, setReceivePaymentOpen] = useState(false);
  const [receivePaymentForm, setReceivePaymentForm] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: '',
    paymentMethod: 'cash',
    paymentAccountId: '',
    referenceNo: '',
    notes: '',
  });
  const [recordSalesPayment, { isLoading: recordingPayment }] = useRecordSalesPaymentMutation();

  const selectedCustomer = customers?.find((c) => c.id === formData.customerId);

  const handleAddProductRow = (product) => {
    if (!product) return;
    setItems((prev) => [
      ...prev,
      {
        productId: product.id,
        productName: product.name,
        productSku: product.sku,
        productBarcode: product.barcode,
        maxDiscountType: product.maxDiscountType === 'percent' || product.maxDiscountType === 'amount' ? product.maxDiscountType : undefined,
        maxDiscountPercent: product.maxDiscountPercent != null ? product.maxDiscountPercent : undefined,
        maxDiscountAmount: product.maxDiscountAmount != null ? product.maxDiscountAmount : undefined,
        quantity: 1,
        unit: 'pcs',
        unitPrice: product.sellingPrice ?? 0,
        discount: 0,
        vatPercent: defaultVat,
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const computeLine = (item) => {
    const qty = toNum(item.quantity);
    const price = toNum(item.unitPrice);
    const discount = toNum(item.discount);
    const vatPct = toNum(item.vatPercent);
    const lineSubtotal = Math.max(0, qty * price - discount);
    let vatAmount = 0;
    if (vatPct > 0) {
      if (vatIncluded) {
        const net = lineSubtotal / (1 + vatPct / 100);
        vatAmount = lineSubtotal - net;
      } else {
        vatAmount = lineSubtotal * (vatPct / 100);
      }
    }
    const lineTotal = vatIncluded ? lineSubtotal : lineSubtotal + vatAmount;
    return {
      vatAmount: Math.round(vatAmount * 100) / 100,
      lineTotal: Math.round(lineTotal * 100) / 100,
      lineSubtotal,
    };
  };

  const orderSubtotal = items.reduce(
    (sum, item) => sum + toNum(item.quantity) * toNum(item.unitPrice),
    0
  );
  const orderDiscountTotal = items.reduce((sum, item) => sum + toNum(item.discount), 0);
  const orderVatTotal = items.reduce((sum, item) => sum + computeLine(item).vatAmount, 0);
  const netOrder = orderSubtotal - orderDiscountTotal;
  const grandTotal = vatIncluded ? netOrder : netOrder + orderVatTotal;

  const paidTotal = payments.reduce(
    (sum, p) => sum + (Number.isFinite(Number(p.amount)) ? Number(p.amount) : 0),
    0
  );
  const remaining = grandTotal - paidTotal;
  const change = paidTotal - grandTotal;

  // If creating a new order and there is a logged-in user, default them as sales person
  useEffect(() => {
    if (!isView && currentUser?.id && !formData.salesPersonId) {
      setFormData((prev) => ({ ...prev, salesPersonId: currentUser.id }));
    }
  }, [isView, currentUser, formData.salesPersonId]);

  // Van mode: auto-set locationId and salesType to cash when user has active van trip
  useEffect(() => {
    if (vanMode && vanLocationId && !isView) {
      setFormData((prev) => ({
        ...prev,
        locationId: vanLocationId,
        salesType: prev.salesType || 'cash',
      }));
    }
  }, [vanMode, vanLocationId, isView]);

  useEffect(() => {
    if (existingOrder && isView) {
      setFormData({
        customerId: existingOrder.customerId || '',
        invoiceNumber: existingOrder.invoiceNumber || '',
        invoiceDate: existingOrder.invoiceDate
          ? existingOrder.invoiceDate.substring(0, 10)
          : formData.invoiceDate,
        orderDate: existingOrder.orderDate
          ? existingOrder.orderDate.substring(0, 10)
          : formData.orderDate,
        salesType: existingOrder.salesType || 'credit',
        creditDays: existingOrder.creditDays != null ? String(existingOrder.creditDays) : '',
        salesPersonId: existingOrder.salesPersonId || '',
        locationId: existingOrder.locationId || '',
        notes: existingOrder.notes || '',
      });
      setItems(
        (existingOrder.items || []).map((it) => ({
          productId: it.productId,
          productName: it.product?.name,
          productSku: it.product?.sku,
          productBarcode: it.product?.barcode,
          maxDiscountType: it.product?.maxDiscountType === 'percent' || it.product?.maxDiscountType === 'amount' ? it.product.maxDiscountType : undefined,
          maxDiscountPercent: it.product?.maxDiscountPercent != null ? it.product.maxDiscountPercent : undefined,
          maxDiscountAmount: it.product?.maxDiscountAmount != null ? it.product.maxDiscountAmount : undefined,
          quantity: it.quantity,
          unit: it.unit || 'pcs',
          unitPrice: it.unitPrice,
          discount: toNum(it.discount),
          vatPercent: toNum(it.vatPercent) || defaultVat,
          batchNo: it.batchNo || '',
          expiryDate: it.expiryDate ? it.expiryDate.substring(0, 10) : '',
        }))
      );
      const paid = toNum(existingOrder.paidAmount);
      if (paid > 0) {
        setPayments([
          { method: 'cash', paymentAccountId: '', referenceNo: '', amount: paid },
        ]);
      }
    }
  }, [existingOrder, isView]);

  // For new cash sales, if there is a single default payment row with empty/zero amount,
  // auto-fill it with the current grand total so the user sees a suggested full payment.
  useEffect(() => {
    if (
      !isView &&
      formData.salesType === 'cash' &&
      payments.length === 1 &&
      (payments[0].amount === '' || Number(payments[0].amount) === 0)
    ) {
      const next = [...payments];
      next[0] = { ...next[0], amount: grandTotal.toFixed(2) };
      setPayments(next);
    }
  }, [grandTotal, formData.salesType, isView, payments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) {
      navigate('/sales');
      return;
    }
    if (!formData.customerId) {
      alert('Customer is required');
      return;
    }
    if (!formData.invoiceDate) {
      alert('Invoice Date is required');
      return;
    }
    if (!items.length) {
      alert('Please add at least one item');
      return;
    }
    if (items.some((i) => !i.productId)) {
      alert('Please select a product for all items');
      return;
    }
    const mode = submitModeRef.current;
    submitModeRef.current = 'save';
    if (formData.salesType === 'cash' && paidTotal + 0.0001 < grandTotal) {
      alert('For cash sales, paid amount must be at least the grand total.');
      return;
    }

    const globalMaxType = company?.maxDiscountType === 'percent' || company?.maxDiscountType === 'amount' ? company.maxDiscountType : null;
    const globalMaxPct = company?.maxDiscountPercent != null ? Number(company.maxDiscountPercent) : null;
    const globalMaxAmt = company?.maxDiscountAmount != null ? Number(company.maxDiscountAmount) : null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const lineMaxType =
        item.maxDiscountType === 'percent' || item.maxDiscountType === 'amount' ? item.maxDiscountType : globalMaxType;
      const lineMaxPct = item.maxDiscountPercent != null ? Number(item.maxDiscountPercent) : globalMaxPct;
      const lineMaxAmt = item.maxDiscountAmount != null ? Number(item.maxDiscountAmount) : globalMaxAmt;
      const discount = toNum(item.discount);
      if (lineMaxType === 'percent' && lineMaxPct != null && lineMaxPct > 0) {
        const qty = toNum(item.quantity);
        const price = toNum(item.unitPrice);
        const lineTotalBeforeDiscount = qty * price;
        if (lineTotalBeforeDiscount > 0 && discount > 0) {
          const lineDiscountPct = (discount / lineTotalBeforeDiscount) * 100;
          if (lineDiscountPct > lineMaxPct) {
            alert(
              `Maximum discount is ${lineMaxPct}%. Line ${i + 1} (${item.productName || 'item'}) has ${lineDiscountPct.toFixed(1)}% discount.`
            );
            return;
          }
        }
      }
      if (lineMaxType === 'amount' && lineMaxAmt != null && lineMaxAmt >= 0 && discount > lineMaxAmt) {
        alert(
          `Maximum discount per line is ${lineMaxAmt} ${currency}. Line ${i + 1} (${item.productName || 'item'}) has discount ${discount}.`
        );
        return;
      }
    }

    try {
      const created = await createSalesOrder({
        ...formData,
        invoiceNumber: formData.invoiceNumber?.trim() || undefined,
        invoiceDate: formData.invoiceDate,
        orderDate: formData.orderDate || formData.invoiceDate,
        salesType: formData.salesType,
        creditDays:
          formData.salesType === 'credit'
            ? (formData.creditDays ? parseInt(formData.creditDays, 10) : defaultCreditDays) ?? undefined
            : undefined,
        salesPersonId: formData.salesPersonId || undefined,
        locationId: formData.locationId || undefined,
        payments: payments.map((p) => ({
          method: p.method,
          paymentAccountId: p.paymentAccountId || undefined,
          referenceNo: p.referenceNo || undefined,
          amount: Number(p.amount || 0),
        })),
        items: items.map((item) => {
          const { vatAmount, lineTotal } = computeLine(item);
          return {
            productId: item.productId,
            quantity: parseFloat(item.quantity),
            unit: item.unit || undefined,
            unitPrice: parseFloat(item.unitPrice),
            discount: toNum(item.discount),
            vatPercent: toNum(item.vatPercent) || defaultVat,
            vatAmount,
            batchNo: item.batchNo || undefined,
            expiryDate: item.expiryDate || undefined,
          };
        }),
      }).unwrap();
      if (mode === 'save-print' && created?.id) {
        navigate(`/sales/orders/${created.id}/print`);
      } else {
        navigate('/sales');
      }
    } catch (error) {
      alert(error?.data?.message || 'Error creating sales order');
    }
  };

  if (isView && loadingOrder) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          {isView ? 'Sales Order Details' : 'New Sales Order'}
        </Typography>
        {isView && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => navigate(`/sales/orders/${id}/print`)} sx={{ borderRadius: 2 }}>
              Print Invoice
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!existingOrder || Number(existingOrder?.balanceAmount || 0) <= 0}
              onClick={() => {
                const balance = Number(existingOrder?.balanceAmount || 0) || 0;
                setReceivePaymentForm((prev) => ({
                  ...prev,
                  paymentDate: new Date().toISOString().split('T')[0],
                  amount: balance > 0 ? balance.toFixed(2) : '',
                  paymentMethod: 'cash',
                  paymentAccountId: '',
                  referenceNo: '',
                  notes: '',
                }));
                setReceivePaymentOpen(true);
              }}
              sx={{ borderRadius: 2 }}
            >
              Receive Payment
            </Button>
          </Box>
        )}
      </Box>

      {vanMode && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            bgcolor: '#E3F2FD',
            border: '1px solid #90CAF9',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <LocalShippingIcon sx={{ color: '#1565C0', fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1565C0' }}>
              Van Sales Mode
            </Typography>
            <Typography variant="body2" sx={{ color: '#1565C0' }}>
              Van: {vanName} ({vanPlate}) &bull; Trip: {vanTripNumber} &bull;
              Sales will be made from the van&apos;s stock and automatically linked to this trip.
            </Typography>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  select
                  label="Customer Name *"
                  name="customerId"
                  value={formData.customerId}
                  onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                  required
                >
                  <MenuItem value="">Select Customer</MenuItem>
                  {customers?.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </TextField>
                {!isView && (
                  <Button
                    variant="outlined"
                    onClick={() => setCustomerDialogOpen(true)}
                    sx={{ height: 56, minWidth: 44 }}
                    title="Add Customer"
                  >
                    <PersonAddAltIcon fontSize="small" />
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Customer TRN (if VAT registered)"
                value={selectedCustomer?.trn ?? ''}
                InputProps={{ readOnly: true }}
                placeholder="From customer"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Invoice Number (auto if empty)"
                name="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                disabled={isView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Invoice Date *"
                name="invoiceDate"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                disabled={isView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Sales Type"
                name="salesType"
                value={formData.salesType}
                onChange={(e) => {
                  const next = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    salesType: next,
                    creditDays: next === 'credit' && defaultCreditDays != null ? String(defaultCreditDays) : prev.creditDays,
                  }));
                }}
                disabled={isView}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
              </TextField>
            </Grid>
            {formData.salesType === 'credit' && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  select
                  label="Credit days (payment due)"
                  name="creditDays"
                  value={formData.creditDays || (defaultCreditDays != null ? String(defaultCreditDays) : '')}
                  onChange={(e) => setFormData({ ...formData, creditDays: e.target.value })}
                  disabled={isView}
                  helperText="Days until payment due"
                >
                  <MenuItem value="">None</MenuItem>
                  {creditDaysOptions.map((days) => (
                    <MenuItem key={days} value={String(days)}>
                      {days} days
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Sales Person (optional)"
                name="salesPersonId"
                value={formData.salesPersonId}
                onChange={(e) => setFormData({ ...formData, salesPersonId: e.target.value })}
                disabled={isView}
              >
                <MenuItem value="">None</MenuItem>
                {userList.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email || u.username}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={vanMode ? `Branch / Store (Van: ${vanName})` : 'Branch / Store'}
                name="locationId"
                value={formData.locationId}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                disabled={isView || !!vanMode}
              >
                <MenuItem value="">None</MenuItem>
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name} ({loc.code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Line Items
                </Typography>
                {!isView && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'center',
                      minWidth: { xs: '100%', sm: 420 },
                      maxWidth: 600,
                    }}
                  >
                    <Autocomplete
                      fullWidth
                      size="medium"
                      autoHighlight
                      options={productSearchData?.products || []}
                      loading={searchingProducts}
                      value={null}
                      inputValue={productSearch}
                      getOptionLabel={(option) =>
                        option?.name ? `${option.name} (${option.sku || option.barcode || ''})` : ''
                      }
                      onInputChange={(_, value) => {
                        setProductSearch(value);
                        if (value && value.length >= 2) {
                          triggerProductSearch({ search: value, limit: 20 });
                        }
                      }}
                      onChange={(_, value) => {
                        if (value) {
                          handleAddProductRow(value);
                          setProductSearch('');
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Scan or search item (name / barcode)"
                          placeholder="Type at least 2 characters"
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 52,
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product Name *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Barcode</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Batch No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Quantity *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Selling Price *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Discount
                        <Typography component="span" variant="caption" display="block" color="text.secondary">
                          {company?.maxDiscountType === 'percent' && company?.maxDiscountPercent != null
                            ? `max ${Number(company.maxDiscountPercent)}% (or product)`
                            : company?.maxDiscountType === 'amount' && company?.maxDiscountAmount != null
                              ? `max ${Number(company.maxDiscountAmount)} ${currency} (or product)`
                              : 'max % or amount if set'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>VAT %</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>VAT Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Line Total</TableCell>
                      {!isView && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const { vatAmount, lineTotal } = computeLine(item);
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              {item.productName || 'Product'}
                              {item.productSku ? ` (${item.productSku})` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            {item.productBarcode ?? '–'}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.batchNo ?? ''}
                              onChange={(e) => handleItemChange(index, 'batchNo', e.target.value)}
                              placeholder="If applicable"
                              sx={{ width: 100 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="date"
                              value={item.expiryDate ?? ''}
                              onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 130 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              disabled={isView}
                              required
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.unit ?? 'pcs'}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                              sx={{ width: 70 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0, step: 0.01 }}
                              disabled={isView}
                              required
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.discount}
                              onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.vatPercent}
                              onChange={(e) => handleItemChange(index, 'vatPercent', e.target.value)}
                              sx={{ width: 60 }}
                              inputProps={{ min: 0, max: 100, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{vatAmount.toFixed(2)}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{lineTotal.toFixed(2)}</TableCell>
                          {!isView && (
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 2 }}>
                Order Summary
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Subtotal (${currency})`}
                value={orderSubtotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Discount Total (${currency})`}
                value={orderDiscountTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`VAT Total (${currency})`}
                value={orderVatTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Grand Total (${currency})`}
                value={grandTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
                sx={{ '& input': { fontWeight: 700 } }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 2 }}>
                Payment
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Account (optional)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Amount (${currency})`}</TableCell>
                      {!isView && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={p.method}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].method = e.target.value;
                              setPayments(next);
                            }}
                            sx={{ minWidth: 120 }}
                            disabled={isView}
                          >
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="bank">Bank</MenuItem>
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="cheque">Cheque</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={p.paymentAccountId || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].paymentAccountId = e.target.value;
                              setPayments(next);
                            }}
                            sx={{ minWidth: 180 }}
                            disabled={isView}
                          >
                            <MenuItem value="">Auto (by method)</MenuItem>
                            {paymentAccounts?.map((acc) => (
                              <MenuItem key={acc.id} value={acc.id}>
                                {acc.code} - {acc.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={p.referenceNo || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].referenceNo = e.target.value;
                              setPayments(next);
                            }}
                            sx={{ minWidth: 140 }}
                            disabled={isView}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={p.amount}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].amount = e.target.value;
                              setPayments(next);
                            }}
                            sx={{ width: 120 }}
                            inputProps={{ min: 0, step: 0.01 }}
                            disabled={isView}
                          />
                        </TableCell>
                        {!isView && (
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setPayments(payments.filter((_, i) => i !== index));
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {!isView && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              setPayments((prev) => [
                                ...prev,
                                {
                                  method: 'cash',
                                  paymentAccountId: '',
                                  referenceNo: '',
                                  amount: grandTotal.toFixed(2),
                                },
                              ])
                            }
                            sx={{ mt: 1 }}
                          >
                            Add Payment Method
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} />
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Paid Total (${currency})`}
                value={paidTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={remaining >= 0 ? `Remaining (${currency})` : `Change (${currency})`}
                value={Math.abs(remaining >= 0 ? remaining : change).toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
                disabled={isView}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  type={isView ? 'button' : 'submit'}
                  variant="contained"
                  disabled={!isView && items.length === 0}
                  onClick={isView ? () => navigate('/sales') : () => { submitModeRef.current = 'save'; }}
                >
                  {isView ? 'Back to Sales' : 'Create Order'}
                </Button>
                {!isView && (
                  <>
                    <Button type="submit" variant="outlined" onClick={() => { submitModeRef.current = 'save-print'; }}>
                      Save &amp; Print
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/sales')}>
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      <Dialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Customer</DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': { overflow: 'visible' },
          }}
        >
          <CustomerFormContent
            compact
            onCancel={() => setCustomerDialogOpen(false)}
            onSuccess={(created) => {
              setCustomerDialogOpen(false);
              if (created?.id) {
                setFormData((prev) => ({ ...prev, customerId: created.id }));
              }
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={receivePaymentOpen}
        onClose={() => setReceivePaymentOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Receive Payment</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            type="date"
            label="Payment Date *"
            value={receivePaymentForm.paymentDate}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, paymentDate: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Method *"
            value={receivePaymentForm.paymentMethod}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))
            }
            fullWidth
          >
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="bank">Bank</MenuItem>
            <MenuItem value="card">Card</MenuItem>
            <MenuItem value="cheque">Cheque</MenuItem>
          </TextField>
          <TextField
            select
            label="Account (optional)"
            value={receivePaymentForm.paymentAccountId}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({
                ...prev,
                paymentAccountId: e.target.value,
              }))
            }
            fullWidth
          >
            <MenuItem value="">Auto (by method)</MenuItem>
            {paymentAccounts?.map((acc) => (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={`Amount (${currency}) *`}
            value={receivePaymentForm.amount}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, amount: e.target.value }))
            }
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label="Reference"
            value={receivePaymentForm.referenceNo}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, referenceNo: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="Notes"
            value={receivePaymentForm.notes}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, notes: e.target.value }))
            }
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pb: 1 }}>
            <Button onClick={() => setReceivePaymentOpen(false)} disabled={recordingPayment}>
              Close
            </Button>
            <Button
              variant="contained"
              disabled={recordingPayment}
              onClick={async () => {
                if (!existingOrder?.id) return;
                const amt = Number(receivePaymentForm.amount || 0);
                if (!amt || amt <= 0) {
                  alert('Amount must be greater than zero');
                  return;
                }
                try {
                  await recordSalesPayment({
                    id: existingOrder.id,
                    paymentDate: receivePaymentForm.paymentDate,
                    amount: amt,
                    paymentMethod: receivePaymentForm.paymentMethod,
                    paymentAccountId:
                      receivePaymentForm.paymentAccountId || undefined,
                    referenceNo: receivePaymentForm.referenceNo || undefined,
                    notes: receivePaymentForm.notes || undefined,
                  }).unwrap();
                  setReceivePaymentOpen(false);
                } catch (err) {
                  alert(err?.data?.message || 'Error recording payment');
                }
              }}
            >
              {recordingPayment ? 'Saving...' : 'Save Payment'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default SalesOrderForm;
